import { useEffect } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import { salesOrderAttachmentsApi } from '#/api/crmMgmt/salesOrder/attachments';
import { fetchSalesOrderDetail } from '#/api/crmMgmt/salesOrder/fetchDetail';
import { ATTACHMENT_POLICY_SPREADSHEET } from '#/api/platform/attachments';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { DocumentAttachmentsCard } from '#/modules/commonComponents/attachments';
import { Button } from '#/modules/commonComponents/button';
import SalesOrderBlock from '#/modules/crmMgmt/salesOrder/components/SalesOrderBlock';
import { getSalesOrderApiErrorMessage } from '#/modules/crmMgmt/salesOrder/helper/apiError';
import PageShell from '#/modules/infra/layout/PageShell';
import { salesOrderDetailQueryKey } from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const SalesOrderDetailPage = () => {
  const { id } = useParams();

  const detailQuery = useQuery({
    queryKey: salesOrderDetailQueryKey(id),
    queryFn: () => fetchSalesOrderDetail(id),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (!detailQuery.isError) {
      return;
    }
    toast.error(getSalesOrderApiErrorMessage(detailQuery.error, '載入銷貨單失敗'));
  }, [detailQuery.isError, detailQuery.error]);

  const detail = detailQuery.data?.data;
  const isLoading = detailQuery.isLoading;
  const isError = detailQuery.isError;

  return (
    <PageShell variant="detail">
      <div className="layout-shell__body">
        {/* <div className="w-full space-y-4"> */}
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <div
            className={cn(
              'sticky top-0 z-20 -mx-1 flex flex-wrap items-center gap-2',
              'border-b border-slate-200 bg-canvas px-1 pb-3 pt-1',
            )}
          >
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">銷貨單詳情</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.salesOrder}>返回列表</Link>
            </Button>
            {detail?.customerOrderId ? (
              <Button type="button" variant="outline" asChild>
                <Link to={ROUTES.customerOrderDetail(detail.customerOrderId)}>
                  返回客訂
                </Link>
              </Button>
            ) : null}
          </div>

          {isLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isError ? (
            <p className="text-sm text-destructive">
              {getSalesOrderApiErrorMessage(detailQuery.error, '載入銷貨單失敗')}
            </p>
          ) : null}

          {detail ? (
            <>
              <SalesOrderBlock
                detail={detail}
                showActions
                onRefetch={() => detailQuery.refetch()}
              />
              <DocumentAttachmentsCard
                businessId={String(detail.id)}
                ownerKey="salesOrder"
                api={salesOrderAttachmentsApi}
                policy={ATTACHMENT_POLICY_SPREADSHEET}
              />
            </>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default SalesOrderDetailPage;
